import flatten from 'flat';

export default flatten({
  insights: {
    disclaimer: "BEEM AI Assistant chats aren't used to train AI models. BEEM AI can make mistakes.",
    headerImg: 'BEEM AI Assistant header image',
    headerTitle: 'What would you like to do?',
    // The title cycles through these verbs, one per sweep of its gradient (EmptyState.tsx).
    headerTitleVerb: 'What would you like to {verb}?',
    headerVerbs: 'do|know|see|create|make',
    headerSubtitle:
      'Discover how to uncover valuable insights from your company data by leveraging AI.',
    info1: 'None of your datasets are currently marked as searchable by BEEM AI.',
    info2: 'Follow the quick and easy steps below to start enjoying the power of AI and your data.',
    onboardingStep1Title: 'Add quality descriptions to your datasets',
    onboardingStep1Text:
      'To get the most out of the AI Assistant, add descriptions to your datasets that express what they contain and explain how to use the data. Navigate to a dataset, inside the editor view and add descriptions from the Documentation panel.',
    onboardingStep2Title: 'Mark your datasets as searchable',
    onboardingStep2Text:
      'In order for BEEM AI to give the most accurate answer to your questions, only marked datasets will be taken into account to generate insights. In the Documentation panel of each dataset, toggle on the searchable option.',
    onboardingStep3Title: 'Ask BEEM AI for insights about your data',
    onboardingStep3Text:
      'Uncover the best insights buried deep inside your datasets by asking the BEEM AI questions.',
    configure: 'Configure My Datasets',
    Chat: {
      placeholder: 'Ask a question, or say what to build',
      copyLink: 'Copy link to this conversation',
      linkCopied: 'Link copied',
      linkCopyFailed: 'Could not copy the link',
      placeholderFollowUp: 'Continue refining your question',
      retry: 'Retry',
      stop: 'Stop',
      inputPlaceholder: 'Type here to chat',
      copy: 'Copy',
      assistantName: 'Robot',
      yourName: 'You',
      send: 'Send',
      roleYou: 'You',
      shareConversation: 'Share',
      shared: 'Shared',
      visibilityManagedByProject: "Visibility is managed by this conversation's project.",
      onlyOwnerCanShare: 'Only the owner ({name}) can share this conversation.',
      shareConversationSuccess: 'Conversation shared with your workspace',
      sharingOff: 'Sharing turned off',
      error: 'There was an error loading the chat messages.',
      disclaimer:
        'The BEEM AI Assistant answers questions leveraging the data contained in the marked datasets of this workspace only.',
      datasetCount:
        '{datasetCount, plural, one {# Dataset} other {# Datasets}} will be accessible by BEEM AI in this conversation.',
      datasetsPopoverTitle: 'Datasets BEEM AI can use',
      selectAllDatasets: 'Select all',
      deselectAllDatasets: 'Deselect all',
      showDescription: 'Show Description',
      datasets: 'Datasets',
      noDatasets: 'No datasets available',
      hideRail: 'Hide panel',
      showRail: 'Show conversation details',
      conversationDetails: 'Conversation Details',
      voiceStart: 'Speak your question',
      voiceStop: 'Stop recording',
      voiceComingSoon: 'Voice input coming soon',
    },
    loading: {
      dashboard: 'Starting your dashboard',
      dashboardRead: 'Reading the dashboard',
      monitoring: 'Reading monitoring events',
      dataset: 'Reading the dataset',
      datasetFix: 'Testing the new SQL',
      dashboardWrite: 'Writing the dashboard',
      dashboardCards: 'Updating the selected cards',
      datasetTest: 'Trying the test on today\'s data',
      source: 'Looking up the source',
      starting: 'Getting started',
      recalling: 'Recalling what I know about you',
      thinking: 'Thinking it through',
      runningQuery: 'Running a query on your data',
      browsing: 'Reading {site}',
      analyzing: 'Reading through your data',
      querying: 'Finding the answer',
      sampling: 'Looking through {datasetName}',
      interpreting: 'Writing it up',
    },
    suggest: {
      label: 'Suggested for your data',
      loading: 'Generating suggestions from your data',
      refresh: 'Regenerate suggestions',
      aboutTitle: 'About {datasetName}',
      aboutDesc: "What it's used for, with a few interesting insights",
      summaryTitle: 'Summarize {datasetName}',
      summaryDesc: 'A quick, high-level overview of the data',
      topValuesTitle: 'Top values in {datasetName}',
      topValuesDesc: 'The most frequent values in the dataset',
    },
    showQuery: 'Show Query',
    showData: 'Show Data',
    copyAnswer: 'Copy answer',
    thumbsDown: 'Bad response',
    feedbackNoted: 'Thanks for your feedback',
    feedback: {
      title: 'What went wrong?',
      incorrect: 'Incorrect answer',
      notFollowed: 'Request not followed',
      bug: 'Something broke (bug)',
      notePlaceholder: 'Add any detail (optional)…',
      submit: 'Send feedback',
      thanks: 'Thanks — this has been flagged for review.',
      disclaimer:
        'To help us look into it, your question and note are shared with the BEEM team, with technical details about this answer. The answer itself and your data are not shared.',
    },
    export: 'Download',
    exportCsv: 'Export CSV',
    exportPdf: 'Export PDF',
    beta: 'BETA',
    close: 'Close',
    createdBy: 'Created by {name}',
    ownedBy: 'Owned by {name}',
    download: {
      title: 'Download conversation',
      subtitle: 'Choose a format to download.',
      zipOption: 'Data tables + transcript',
      zipDesc: "Each answer's table as a spreadsheet (CSV), plus the conversation text, bundled in a .zip.",
      pdfOption: 'PDF document',
      pdfDesc: 'The whole conversation as a printable PDF, tables included.',
    },
    home: {
      recent: 'Recent conversations',
    },
    panelCta: {
      monitor: 'What failed recently, and why?',
      dashboard: 'Help me analyze this dashboard',
      dataset: "Explain what's in this dataset",
      dataObject: 'Summarize this data',
      flow: 'Explain what this flow does',
      generic: "What can you tell me about what I'm looking at?",
    },
    tools: {
      dashboard: 'Dashboard',
      dashboardRead: 'Read a dashboard',
      openDashboard: 'Open the dashboard',
      dashboardBuilding: 'Building, usually a few minutes',
      dashboardReady: 'Ready',
      dashboardFailed: 'Generation failed, open it to see why',
      monitor: 'Monitoring events',
      monitorReading: 'Reading monitoring events',
      monitorRead: '{count, plural, one {Read # event} other {Read # events}}',
      dataset: 'Dataset',
      datasetRead: 'Read {name}',
      datasetFix: 'Proposed SQL',
      datasetFixTested: 'Tested, {count, plural, one {# column} other {# columns}}',
      reviewFix: 'Review in the editor',
      reviewNew: 'Create the dataset',
      fixHint: 'Not saved yet: you review and deploy it',
      datasetTest: 'Proposed test',
      testPassesToday: 'passes today',
      testFailsToday: 'fails today',
      testHintPasses: 'Test on {dataset}. Passes on today\'s data.',
      testHintFails: 'Test on {dataset}. Fails on today\'s data, so its next results would be blocked.',
      connectedBackToChat: 'Source connected. Its data arrives after the first sync.',
      connectedFollowUp: 'I connected {name}. What can we do with it?',
      theSource: 'the source',
      backToChat: 'Finishing the connection, then back to your conversation…',
      page: 'Saved the dashboard',
      pageProblems: '{count, plural, one {# problem to fix} other {# problems to fix}}',
      createTest: 'Create the test',
      newDatasetHint: 'Created and deployed when you click, in Datasets under AI Assistant',
      reviewFirst: 'Review it first',
      creatingDataset: 'Creating…',
      openDataset: 'Open the dataset',
      datasetStage: {
        creating: 'Creating the dataset',
        deploying: 'Deploying it to the warehouse',
        running: 'Deployed, first refresh running',
        ready: 'Ready, first refresh done',
        readyCounts:
          'Ready, first refresh done: {rows, plural, one {# row} other {# rows}}, {columns, plural, one {# column} other {# columns}}',
        failed: 'The first refresh failed, open it to see why',
        draft: 'Saved as a draft: review and deploy it in the editor',
      },
      creatingTest: 'Creating…',
      source: 'Source',
      connect: 'Connect {name}',
      connectHint: 'Opens setup to authorize access. Data arrives after the first sync.',
    },
    source: {
      createdBy: 'Created by the AI Assistant',
      openConversation: 'Open the conversation',
    },
    dashboardChat: {
      empty: 'Ask for what this dashboard should show, or select cards on it and say what to change.',
      placeholder: 'Describe what to build or change…',
      readOnly: "Only this dashboard's creator can change it with AI for now. You can still view it and ask questions in the AI Assistant.",
    },
    context: {
      monitor: 'Monitor',
      datalake: 'Data Lake',
      warehouse: 'Warehouse',
      dashboard: 'Dashboard',
      aiDashboard: 'AI dashboard',
      flows: 'Flows',
      home: 'Home',
      settings: 'Settings',
      other: 'Panel',
    },
    panel: {
      earlierHere: 'Earlier here',
    },
    memory: {
      open: 'Memory',
      title: 'Memory',
      intro:
        'BEEM AI learns facts and preferences from your conversations in this workspace, and keeps a short summary of each conversation, so it can answer you better next time. Only you can see them. Deleting one does not stop it from being learned again if it comes up in a later conversation.',
      notesHint: 'Tell BEEM AI something to keep in mind in every conversation.',
      stepLabel: 'Memory',
      recalled: '{count, plural, one {Recalled # thing} other {Recalled # things}} I remember about you that may be relevant.',
      seeWhat: 'See what I remember',
      disabled: 'Memory is not turned on for this workspace yet.',
      loadError: "We couldn't load what BEEM AI remembers. Please try again.",
      saveError: "Your change wasn't saved. Please try again.",
      addError: "That wasn't added to memory. Please try again.",
      addPlaceholder: 'Remember that…',
      add: 'Add',
      empty: 'Nothing remembered yet.',
      edit: 'Edit',
      delete: 'Delete',
      save: 'Save',
      cancel: 'Cancel',
      loadMore: 'Load more',
      forgetAll: 'Forget everything',
      forgetAllTitle: 'Forget everything?',
      forgetAllBody:
        "BEEM AI will erase {facts, plural, one {# fact} other {# facts}}, {preferences, plural, one {# preference} other {# preferences}}, {sessions, plural, one {# conversation summary} other {# conversation summaries}} and {notes, plural, one {# note} other {# notes}} it keeps about you in this workspace. Your conversations themselves are kept. This can't be undone.",
      forgetAllConfirm: 'Type {word} to confirm',
      forgetAllInProgress: 'Forgetting everything. This can take a few minutes to complete.',
      kind: {
        all: 'All',
        facts: 'Facts',
        preferences: 'Preferences',
        sessions: 'Conversations',
        notes: 'Your notes',
      },
    },
    history: {
      title: 'Conversations',
      new: 'New conversation',
      rename: 'Rename',
      delete: 'Delete this conversation?',
      deleteBody: "This conversation and all of its messages will be permanently deleted. This can't be undone.",
      empty: 'No conversations yet',
      sharedSection: 'Shared to workspace',
      recentSection: 'Recent',
      sharedWithYou: 'Shared with you',
      stopSharing: 'Stop sharing',
      deleteAction: 'Delete',
      projectsSection: 'Projects',
      newProject: 'New project',
      moveToProject: 'Move to project',
      moveBody: "Move this conversation into a project. It will inherit the project's default datasets and visibility.",
      move: 'Move',
      removeFromProject: 'Remove from project',
      searchConversations: 'Search conversations',
      searchProjects: 'Search projects',
    },
    project: {
      createTitle: 'New project',
      editTitle: 'Edit project',
      untitled: 'Untitled Project',
      makePublic: 'Make public',
      makePrivate: 'Make private',
      name: 'Project Name',
      namePlaceholder: 'e.g. Q3 Forecast',
      description: 'Description',
      descriptionPlaceholder: 'What is this project about?',
      instructions: 'Custom instructions',
      instructionsPlaceholder: 'Context every conversation in this project should use…',
      datasets: 'Default datasets',
      create: 'Create project',
      save: 'Save',
      usingContext: 'Using project context',
      usingContextAndDatasets: 'Using context and datasets defined by this project',
      settings: 'Project settings',
      details: 'Project Details',
      edit: 'Edit project settings',
      conversations: 'Conversations',
      empty: 'No conversations yet — start one below.',
      noProjects: 'No projects yet',
      ask: 'Ask a question about this project…',
      delete: 'Delete project',
      deleteConfirm: 'Delete this project?',
      deleteConfirmBody: 'Its conversations will be kept and moved out of this project.',
      datasetCount: '{count, plural, =0 {No default datasets} one {# default dataset} other {# default datasets}}',
      noInstructions: 'No custom instructions yet',
      commonPromptPlaceholder: 'Describe this project and any instructions every conversation in it should follow…',
      noPrompt: 'No description yet',
      noKnowledge: 'No knowledge added yet',
      knowledge: 'Knowledge',
      knowledgePlaceholder: 'Reference material fed to conversations started in this project…',
      visibility: 'Visibility',
      private: 'Private',
      privateDesc: 'Only you can see and use this project.',
      public: 'Public',
      publicDesc: 'Everyone in the workspace can view and edit this project.',
      visibilityPermanent:
        'Visibility is permanent — it <b>CANNOT</b> be changed after the project is created.',
    },
    share: {
      title: 'Share conversation',
      subtitle:
        'This conversation will be shared with everyone in your workspace. Optionally, pick people below to email a notification to — or just Share.',
      tabMembers: 'Workspace members',
      tabInvite: 'Invite to workspace',
      searchMembers: 'Search members',
      notifying: 'Notifying',
      noMembers: 'No members to notify',
      enterEmail: "Enter the new users' emails",
      inviteWarning:
        'Confirming will invite {count, plural, one {# external user} other {# external users}} to this workspace.',
      alreadyMembers: 'Already in this workspace: {emails}',
      fixEmails: 'Please correct the errors in the email fields.',
      cancel: 'Cancel',
      notify: 'Notify',
      confirm: 'Share',
      success: 'Conversation shared',
      stopConfirm: 'Stop sharing this conversation?',
      stopConfirmBody:
        'This conversation will no longer be visible to your workspace. You can share it again at any time.',
    },
  },
});
